import {getSizedImageUrl} from '../vendor/theme-scripts/theme-images';
import {getUrlWithVariant} from '../vendor/theme-scripts/theme-product-form';
import {fetchProduct} from '../util/fetch-product';
import loadScript from '../util/loader';

const defaults = {
  color: 'ash',
};

const selectors = {
  swatch: '[data-swatch]',
  swatchColor: '[data-swatch-color]',
  template: '[data-swatch-template]',
  productBlock: '[data-product-block]',
  productImage: '[data-product-image]',
  productImageSecondary: '[data-product-image-secondary]',
  productImageHover: '[data-product-image-hover]',
  productId: '[data-product-id]',
  quickView: '[data-button-quick-view]',
  gridImage: '[data-grid-image]',
  link: '[data-grid-link]',
  loadHovers: '[data-load-hovers]',
};

const classes = {
  mediaVisible: 'product__media--featured-visible',
  mediaHoverVisible: 'product__media__hover-img--visible',
  noImage: 'swatch__link--no-image',
};

const attributes = {
  swatch: 'data-swatch',
  handle: 'data-swatch-handle',
  label: 'data-swatch-label',
  image: 'data-swatch-image',
  imageId: 'data-swatch-image-id',
  variant: 'data-swatch-variant',
  index: 'data-swatch-index',
  productId: 'data-product-id',
  swatchVariant: 'data-swatch-variant',
  variandId: 'data-variant-id',
  href: 'href',
  bgSet: 'data-bgset',
  aspectRatio: 'data-aspectratio',
  dataFetchedImage: 'data-fetched-image',
  dataFetchedImageIndex: 'data-fetched-image-index',
  dataGridImageDefault: 'data-grid-image-default',
  dataGridImageTarget: 'data-grid-image-target',
  dataGridImageTargetDefault: 'data-grid-image-target-default',
};

let swatches = {};

class ColorMatch {
  constructor(options = {}) {
    this.settings = {
      ...defaults,
      ...options,
    };

    this.match = this.init();
  }

  getColor() {
    return this.match;
  }

  init() {
    const getColors = loadScript({json: theme.assets.swatches});
    return getColors
      .then((colors) => {
        return this.matchColors(colors, this.settings.color);
      })
      .catch((e) => {
        console.log('failed to load swatch colors script');
        console.log(e);
      });
  }

  matchColors(colors, name) {
    let bg = '#E5E5E5';
    let img = null;
    const path = theme.assets.base || '/';
    const comparisonName = name.toLowerCase().replace(/\s/g, '');
    const array = colors.colors;

    if (array) {
      let indexArray = null;

      const hexColorArr = array.filter((colorObj, index) => {
        const neatName = Object.keys(colorObj).toString().toLowerCase().replace(/\s/g, '');

        if (neatName === comparisonName) {
          indexArray = index;

          return colorObj;
        }
      });

      if (hexColorArr.length && indexArray !== null) {
        const value = Object.values(array[indexArray])[0];
        bg = value;

        if (value.includes('.jpg') || value.includes('.jpeg') || value.includes('.png') || value.includes('.svg')) {
          img = `${path}${value}`;
          bg = '#888888';
        }
      }
    }

    return {
      color: this.settings.color,
      path: img,
      hex: bg,
    };
  }
}

class Swatch {
  constructor(element) {
    this.element = element;
    this.swatchLink = this.element.nextElementSibling;
    this.colorString = element.getAttribute(attributes.swatch);
    this.image = this.element.getAttribute(attributes.image);
    this.imageId = this.element.getAttribute(attributes.imageId);
    this.variant = this.element.getAttribute(attributes.variant);
    this.outer = this.element.closest(selectors.productBlock);
    this.gridImage = null;
    this.imageDefault = null;
    this.hoverImages = [];
    this.loadHovers = null;

    const matcher = new ColorMatch({color: this.colorString});
    matcher.getColor().then((result) => {
      this.colorMatch = result;
      this.init();
    });
  }

  init() {
    this.setStyles();

    if (this.variant && this.outer) {
      this.handleHovers();
      this.handleClicks();
    }

    if (!this.image && this.swatchLink) {
      this.swatchLink.classList.add(classes.noImage);
    }
  }

  setStyles() {
    if (this.colorMatch && this.colorMatch.hex) {
      this.element.style.setProperty('--swatch', `${this.colorMatch.hex}`);
    }

    if (this.colorMatch && this.colorMatch.path) {
      this.element.style.setProperty('background-image', `url(${this.colorMatch.path})`);
    }
  }

  handleHovers() {
    // Load images on PGI swatch hover
    this.swatchLink.addEventListener('mouseenter', () => {
      this.imageReplace = null;

      if (this.imageId) {
        const target = this.outer.querySelector(`[${attributes.dataGridImageTarget}="${this.imageId}"]`);
        if (!target) {
          this.gridImage = this.outer.querySelector(selectors.gridImage);

          if (this.image && this.gridImage) {
            // Container width rounded to the nearest 180 pixels
            // Increses likelihood that the image will be cached
            const ratio = window.devicePixelRatio || 1;
            const pixels = this.gridImage.offsetWidth * ratio;
            const widthRounded = Math.ceil(pixels / 180) * 180;
            const defaultImageId = this.gridImage.hasAttribute(attributes.dataGridImageTargetDefault) ? this.gridImage.getAttribute(attributes.dataGridImageTargetDefault) : '';
            if (defaultImageId === this.imageId && this.gridImage.hasAttribute(attributes.dataGridImageDefault)) {
              // Get default loaded image
              this.imageReplace = this.gridImage.getAttribute(attributes.dataGridImageDefault);
              return;
            }

            if (this.element.hasAttribute(attributes.dataFetchedImage)) {
              // Get already loaded image
              this.imageReplace = this.element.getAttribute(attributes.dataFetchedImage);
            } else {
              // Fetch new image
              const sizedImage = getSizedImageUrl(this.image, `${widthRounded}x`);

              window
                .fetch(sizedImage)
                .then((response) => {
                  return response.blob();
                })
                .then((blob) => {
                  const objectURL = URL.createObjectURL(blob);
                  this.imageReplace = `url("${objectURL}")`;
                  this.element.setAttribute(attributes.dataFetchedImage, this.imageReplace);

                  if (
                    this.element.hasAttribute(attributes.index) &&
                    this.outer.hasAttribute(attributes.dataFetchedImageIndex) &&
                    parseInt(this.element.getAttribute(attributes.index)) === parseInt(this.outer.getAttribute(attributes.dataFetchedImageIndex))
                  ) {
                    this.replaceImages();

                    this.outer.removeAttribute(attributes.dataFetchedImageIndex);
                  }
                })
                .catch((error) => {
                  console.log(`Error: ${error}`);
                });
            }
          }
        }
      }

      this.loadHovers = this.outer.querySelector(selectors.loadHovers);

      if (this.loadHovers && !this.loadHovers?.hasAttribute('data-loaded')) {
        const content = document.createElement('div');
        content.appendChild(this.loadHovers.querySelector('template').content.firstElementChild.cloneNode(true));
        this.loadHovers.appendChild(content);
        this.loadHovers.setAttribute('data-loaded', true);
      }
    });
  }

  handleClicks() {
    // Change PGI featured image on swatch click
    this.swatchLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.updateImagesAndLinksOnEvent();
    });

    this.swatchLink.addEventListener('keyup', (event) => {
      if (event.code !== theme.keyboardKeys.ENTER) {
        return;
      }
      event.preventDefault();
      this.updateImagesAndLinksOnEvent();
    });
  }

  updateImagesAndLinksOnEvent() {
    this.updateLinks();
    this.replaceImages();
  }

  updateLinks() {
    this.linkElements = this.outer.querySelectorAll(selectors.link);
    this.quickView = this.outer.querySelector(selectors.quickView);

    // Update links
    if (this.linkElements.length) {
      this.linkElements.forEach((element) => {
        const destination = getUrlWithVariant(element.getAttribute('href'), this.variant);
        element.setAttribute('href', destination);
      });
    }

    // Change quickview variant with swatch one
    if (this.quickView && theme.settings.quickBuy === 'quick_buy') {
      this.quickView.setAttribute(attributes.variandId, this.variant);
    }
  }

  replaceImages() {
    this.imageSecondary = this.outer.querySelector(selectors.productImageSecondary);
    this.outer.removeAttribute(attributes.dataFetchedImageIndex);

    if (!this.imageReplace && this.element.hasAttribute(attributes.index)) {
      this.outer.setAttribute(attributes.dataFetchedImageIndex, parseInt(this.element.getAttribute(attributes.index)));
    }

    // Add new loaded image and sync with the secondary image for smooth animation
    if (this.imageReplace && this.gridImage && this.imageId) {
      this.gridImage.setAttribute(attributes.dataGridImageTarget, this.imageId);

      if (!this.gridImage.hasAttribute(attributes.dataGridImageDefault)) {
        this.gridImage.setAttribute(attributes.dataGridImageDefault, window.getComputedStyle(this.gridImage).backgroundImage);
      }

      const onAnimationEnd = () => {
        requestAnimationFrame(() => {
          this.gridImage.style.setProperty('background-image', this.imageReplace);

          requestAnimationFrame(() => {
            this.imageSecondary.classList.remove(classes.mediaVisible);
          });
        });

        this.imageSecondary.removeEventListener('animationend', onAnimationEnd);
      };

      requestAnimationFrame(() => {
        this.imageSecondary.classList.add(classes.mediaVisible);
        this.imageSecondary.style.setProperty('background-image', this.imageReplace);
      });

      this.imageSecondary.addEventListener('animationend', onAnimationEnd);
    }

    // Change all hover images classes
    if (theme.settings.productGridHover === 'image') {
      this.hoverImages = this.outer.querySelectorAll(selectors.productImageHover);
    }

    if (this.hoverImages.length > 1) {
      this.hoverImages.forEach((hoverImage) => {
        hoverImage.classList.remove(classes.mediaHoverVisible);

        if (hoverImage.getAttribute(attributes.variandId) === this.variant) {
          hoverImage.classList.add(classes.mediaHoverVisible);
        } else {
          this.hoverImages[0].classList.add(classes.mediaHoverVisible);
        }
      });
    }
  }
}

class GridSwatch extends HTMLElement {
  constructor() {
    super();

    this.handle = this.getAttribute(attributes.handle);
    this.label = this.getAttribute(attributes.label).trim().toLowerCase();

    fetchProduct(this.handle).then((product) => {
      this.product = product;
      this.colorOption = product.options.find((element) => {
        return element.name.toLowerCase() === this.label || null;
      });

      if (this.colorOption) {
        this.swatches = this.colorOption.values;
        this.init();
      }
    });
  }

  init() {
    this.swatchElements = this.querySelectorAll(selectors.swatch);

    this.swatchElements.forEach((el) => {
      new Swatch(el);
    });
  }
}

const makeSwatches = (container) => {
  swatches = [];
  const els = container.querySelectorAll(selectors.swatch);
  els.forEach((el) => {
    swatches.push(new Swatch(el));
  });
};

const swatchSection = {
  onLoad() {
    makeSwatches(this.container);
  },
};

export {swatchSection, makeSwatches, GridSwatch};
